import React, {useRef, useState} from 'react'
import styled from 'styled-components'
import {MainLayoutWithoutStretch, MainContent} from '../../../Layout/Layout'
import {HeaderRow, GoBackHeaderTitle} from '../../../Molecules/ViewComponents/ViewHeader'
import {Baseline} from '../../../Layout/Grid'
import {useTranslation} from 'react-i18next'
import {Button} from '../../../Atoms/Buttons'
import {H4, H5, P} from '../../../Atoms/Typography'
import {colors} from '../../../../sharedComponents/colors'
import SiteSelectionCheckboxStyle from '../../../Organisms/SiteSelectionCheckboxStyle'
import {useAppState} from '../../../../state'
import {SwitchReversed} from '../../../../sharedComponents/components'
import {useNavigation} from 'react-navi'

type Holiday = {
  id: string
  date: string
  localName: string
  name: string
  countryCode: string
  global: boolean
  counties: string[] | null
  launchYear: number | null
  types: string[]
  selected: boolean
}

type PublicHolidayStatusMessage =
  | {type: 'fetchError'; text: string}
  | {type: 'saveError'; text: string}
  | {type: 'overlap'; siteNames: string[]}
  | null

type PublicHolidaySuccessMessage = {createdCount: number} | null

const regionCountryMap: {[region: string]: {code: string; label: string}} = {
  fi: {code: 'FI', label: 'Finland'},
  se: {code: 'SE', label: 'Sweden'},
  no: {code: 'NO', label: 'Norway'}
}

const currentYear = new Date().getFullYear()

const formatHolidayDate = (date: string, countryCode: string) => {
  const d = new Date(date)

  switch (countryCode) {
    case 'FI':
      return d.toLocaleDateString('fi-FI', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric'
      })

    case 'SE':
      return d.toLocaleDateString('sv-SE', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric'
      })

    default:
      return date
  }
}

const PublicHolidayClosingPeriodPage = () => {
  const {state, actions} = useAppState()
  const nav = useNavigation()
  const {t} = useTranslation(['locations', 'settings', 'common'])
  const mainContentRef = useRef<HTMLDivElement>(null)
  const [holidays, setHolidays] = useState<Holiday[]>([])
  const [selectedSites, setSelectedSites] = useState<string[]>([])
  const [isChainWide, setIsChainWide] = useState(false)
  const [alarmsEnabled, setAlarmsEnabled] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<PublicHolidayStatusMessage>(null)
  const [successMessage, setSuccessMessage] = useState<PublicHolidaySuccessMessage>(null)

  const chain = Object.values(state.chainsById).find(({id}) => id === state.selectedChainId)!
  const selectedRegion = chain.region ? regionCountryMap[chain.region.toLowerCase()] : undefined
  let writableIds = state.me?.accessRights?.read.sites
  writableIds = writableIds?.concat(...(state.me?.accessRights?.write.chains ?? []))

  const getHolidays = async () => {
    setIsLoading(true)
    setErrorMessage(null)
    setSuccessMessage(null)

    try {
      if (!selectedRegion) {
        throw new Error(
          t(
            'locations:messages.publicHolidayRegionMissing',
            'Selected chain does not have a supported region for public holidays.'
          )
        )
      }

      const result = await actions.v1.settings.multipleSiteSuspendedPeriod.getPublicHolidays({
        countryCode: selectedRegion.code,
        year: currentYear
      })

      setHolidays(
        (result || []).map((holiday, index) => ({
          id: `${holiday.date}-${holiday.name}-${index}`,
          date: holiday.date,
          localName: holiday.localName || holiday.name,
          name: holiday.name,
          countryCode: holiday.countryCode,
          global: true,
          counties: null,
          launchYear: null,
          types: [],
          selected: true
        }))
      )
    } catch (error) {
      setHolidays([])
      setErrorMessage(
        error instanceof Error
          ? {type: 'fetchError', text: error.message}
          : {
              type: 'fetchError',
              text: t(
                'locations:messages.publicHolidayFetchFailed',
                'Failed to fetch public holidays. Please try again.'
              )
            }
      )
    } finally {
      setIsLoading(false)
    }
  }

  const addHolidaysToSites = async () => {
    const selectedHolidays = holidays.filter(holiday => holiday.selected)

    if (selectedHolidays.length === 0 || selectedSites.length === 0) {
      return
    }

    mainContentRef.current?.scrollTo(0, 0)
    setIsSubmitting(true)
    setErrorMessage(null)
    setSuccessMessage(null)

    try {
      const response = await actions.v1.settings.multipleSiteSuspendedPeriod.newPublicHolidaySuspendedPeriod({
        alarmsEnabled,
        sites: selectedSites.filter(siteId => siteId !== chain.id),
        holidays: selectedHolidays.map(holiday => ({
          date: holiday.date,
          localName: holiday.localName,
          name: holiday.name,
          countryCode: holiday.countryCode
        }))
      })

      if (!response) {
        throw new Error(t('locations:messages.publicHolidaySaveFailed', 'Failed to save public holidays to sites.'))
      }

      if (response.skippedCount > 0) {
        const overlappingSiteNames = Array.from(
          new Set(
            response.data
              .filter(info => info.hasOverlappingPeriod)
              .map(info => info.siteName)
              .filter(Boolean)
          )
        )
        const skippedSiteNames = Array.from(
          new Set(
            response.data
              .filter(info => info.hasOverlappingPeriod || info.created === false)
              .map(info => info.siteName)
              .filter(Boolean)
          )
        )

        if (response.createdCount > 0) {
          setSuccessMessage({createdCount: response.createdCount})
        }

        setErrorMessage({
          type: 'overlap',
          siteNames: overlappingSiteNames.length > 0 ? overlappingSiteNames : skippedSiteNames
        })
      } else {
        setSuccessMessage({createdCount: response.createdCount})
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? {type: 'saveError', text: error.message}
          : {
              type: 'saveError',
              text: t('locations:messages.publicHolidaySaveFailed', 'Failed to save public holidays to sites.')
            }
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleHoliday = (holidayId: string) => {
    setHolidays(currentHolidays =>
      currentHolidays.map(holiday => (holiday.id === holidayId ? {...holiday, selected: !holiday.selected} : holiday))
    )
  }

  const isSubmitDisabled =
    holidays.filter(holiday => holiday.selected).length === 0 ||
    selectedSites.filter(siteId => siteId !== chain.id).length === 0 ||
    isSubmitting

  const renderedErrorMessage =
    errorMessage?.type === 'overlap'
      ? `${t(
          'settings:description.overlappingSitesErrorMessage',
          'Overlapping periods found for sites'
        )}: [ ${errorMessage.siteNames.join(', ')} ]`
      : errorMessage && 'text' in errorMessage
      ? errorMessage.text
      : null

  const renderedSuccessMessage = successMessage
    ? t(
        'locations:messages.publicHolidaySaveSuccess',
        'Added {{createdCount}} holiday closing periods to selected sites.',
        {
          createdCount: successMessage.createdCount
        }
      )
    : null

  return (
    <MainLayoutWithoutStretch>
      <MainContent ref={mainContentRef} variant="white">
        <HeaderRow>
          <GoBackHeaderTitle
            path="/settings/locations"
            label={t('locations:title.addPublicHolidayClosingPeriod', 'Add site closing period for public holiday')}
            backLabel={t('locations:labels.locations', 'Location settings')}
          />
        </HeaderRow>
        <PageContent>
          <Baseline>
            <ContentColumn>
              <Section>
                <H4>{t('locations:labels.publicHolidayCountry', 'Country')}</H4>
                <Description>
                  {selectedRegion
                    ? selectedRegion.label
                    : t(
                        'locations:messages.publicHolidayRegionMissing',
                        'Selected chain does not have a supported region for public holidays.'
                      )}
                </Description>
                <ButtonRow>
                  <Button variant="secondary" onClick={getHolidays} disabled={isLoading || !selectedRegion}>
                    {isLoading
                      ? t('locations:actions.loadingHolidays', 'Getting holidays...')
                      : t('locations:actions.getHoliday', 'Get Holidays')}
                  </Button>
                </ButtonRow>
                {renderedErrorMessage && <ErrorText>{renderedErrorMessage}</ErrorText>}
                {renderedSuccessMessage && <SuccessText>{renderedSuccessMessage}</SuccessText>}
              </Section>

              <Section>
                <Description>
                  {t(
                    'locations:messages.publicHolidayListDescription',
                    'Fetched public holidays for the selected country are shown below.'
                  )}
                </Description>
                <HolidayList>
                  {holidays.map(holiday => (
                    <HolidayCard key={holiday.id}>
                      <HolidayDate>{formatHolidayDate(holiday.date, holiday.countryCode)}</HolidayDate>
                      <HolidayTextWrap>
                        <H5>{holiday.localName || ''}</H5>
                      </HolidayTextWrap>
                      <HolidayCheckbox
                        type="checkbox"
                        checked={holiday.selected}
                        onChange={() => toggleHoliday(holiday.id)}
                        aria-label={holiday.localName || holiday.name}
                      />
                    </HolidayCard>
                  ))}
                </HolidayList>
              </Section>

              <Section>
                <SwitchRow>
                  <SwitchTextWrap>
                    <H5>{t('settings:labels.columnNameApplianceAlarms', 'Appliance alarms')}</H5>
                    <P variant="small">
                      {t(
                        'locations:messages.publicHolidayApplianceAlarmDescription',
                        'Disable or enable appliance alarms for the holiday closing periods you are about to add.'
                      )}
                    </P>
                  </SwitchTextWrap>
                  <SwitchReversed checked={!alarmsEnabled} onChange={() => setAlarmsEnabled(!alarmsEnabled)} />
                </SwitchRow>
                <ButtonRow>
                  <Button variant="secondary" onClick={addHolidaysToSites} disabled={isSubmitDisabled}>
                    {isSubmitting
                      ? t('locations:actions.addingHolidaysToSites', 'Adding holidays to sites...')
                      : t('locations:actions.addHolidaysToSites', 'Add holidays to selected sites')}
                  </Button>
                  <SecondaryButton
                    variant="secondary"
                    negative
                    onClick={() => nav.navigate('/settings/locations')}
                    disabled={isSubmitting}
                  >
                    {t('common:buttons.cancel', 'Cancel')}
                  </SecondaryButton>
                </ButtonRow>
              </Section>
            </ContentColumn>
          </Baseline>
        </PageContent>
      </MainContent>
      <SiteSelectionCheckboxStyle
        infoText={t('locations:title.selectSitesToaddClosingPeriod', 'Select sites to add closing period')}
        writableIds={writableIds || []}
        chain={chain}
        isLoading={false}
        isChainWide={isChainWide}
        selectedSites={selectedSites}
        setIsChainWide={setIsChainWide}
        setSelectedSites={setSelectedSites}
        t={t}
      />
    </MainLayoutWithoutStretch>
  )
}

const PageContent = styled.div`
  margin: 2rem;
`

const Section = styled.section`
  margin-bottom: 2rem;
`

const ContentColumn = styled.div`
  width: 100%;
  max-width: 46rem;
`

const ButtonRow = styled.div`
  margin-top: 1.5rem;
`

const Description = styled.p`
  color: ${colors.system.grey_50};
  margin: 1rem 0;
`

const EmptyState = styled.div`
  background: ${colors.system.lightGrey_5};
  border: 1px solid ${colors.system.grey_4};
  color: ${colors.system.grey_50};
  padding: 1rem;
`

const HolidayList = styled.div`
  display: grid;
  gap: 0.75rem;
`

const HolidayCard = styled.div`
  border: 1px solid ${colors.system.grey_4};
  border-radius: 6px;
  padding: 0.75rem 1rem;
  background: ${colors.system.white};
  display: grid;
  grid-template-columns: 8.5rem minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: start;
`

const HolidayCheckbox = styled.input`
  margin-top: 0.2rem;
`

const HolidayDate = styled.div`
  color: ${colors.brand.cobalt};
  font-family: FavoritMonoStd-Regular, monospace;
  font-size: 0.8rem;
  line-height: 1.2rem;
`

const HolidayTextWrap = styled.div`
  min-width: 0;

  h5 {
    margin: 0;
  }
`

const ErrorText = styled.div`
  color: ${colors.system.red};
  margin-top: 1rem;
`

const SuccessText = styled.div`
  color: ${colors.system.green};
  margin-top: 1rem;
`

const SwitchRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  padding: 0.875rem 1rem;
  border: 1px solid ${colors.system.grey_4};
  border-radius: 6px;
  margin-top: 1rem;
`

const SwitchTextWrap = styled.div`
  h5 {
    margin: 0 0 0.25rem 0;
  }

  p {
    margin: 0;
    color: ${colors.system.grey_50};
  }
`

const SecondaryButton = styled(Button)`
  margin-left: 1rem;
`

export default PublicHolidayClosingPeriodPage
